import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../../contexts/DataContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const { FiArrowLeft, FiPlus, FiEdit, FiTrash2, FiShield, FiBriefcase, FiUsers, FiCheck, FiX, FiInfo } = FiIcons;

const ExternalServicesManagement = () => {
  const navigate = useNavigate();
  const context = useData();
  
  // SIKKERHETSLAG: Tvinger frem standardtjenester visuelt hvis listen er tom
  const rawServices = Array.isArray(context?.externalServices) ? context.externalServices : [];
  
  // Hardkodet backup for visning hvis alt feiler (som du opplevde)
  const BACKUP_SERVICES = [
    {
      id: 'external-single',
      name: 'Enkelt varsel (Ekstern)',
      price: 6900,
      timeframe: 'sak',
      description: 'Engangshåndtering av én sak av ekstern utreder.',
      category: 'external',
      active: true,
      icon: 'FiShield'
    },
    {
      id: 'external-partner',
      name: 'Varslingspartner',
      price: 1990,
      timeframe: 'mnd',
      description: 'Fast avtale om eksternt varslingsmottak.',
      category: 'external',
      active: true,
      icon: 'FiBriefcase'
    }
  ];

  // Bruk backup hvis listen er tom, ellers bruk context
  const servicesToRender = rawServices.length > 0 ? rawServices : BACKUP_SERVICES;

  const { addExternalService, updateExternalService, deleteExternalService } = context;
  const companies = Array.isArray(context?.companies) ? context.companies : [];

  const [activeTab, setActiveTab] = useState('services');
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [serviceFormData, setServiceFormData] = useState({
    name: '',
    type: 'subscription',
    description: '',
    price: '',
    category: 'external',
    features: [''],
    timeframe: 'mnd',
    icon: 'FiShield',
    active: true
  });

  const handleServiceSubmit = (e) => {
    e.preventDefault();
    const data = { ...serviceFormData, price: parseFloat(serviceFormData.price) || 0 };
    if (selectedService) {
      updateExternalService(selectedService.id, data);
    } else {
      addExternalService(data);
    }
    setShowServiceModal(false);
  };

  const getIconComponent = (name) => {
    const map = { FiShield, FiBriefcase, FiUsers };
    return map[name] || FiShield;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b p-4 flex items-center gap-4 sticky top-0 z-10">
        <button onClick={() => navigate('/superadmin')} className="p-2 hover:bg-gray-100 rounded-lg"><SafeIcon icon={FiArrowLeft} /></button>
        <h1 className="text-xl font-bold">Eksterne Tjenester</h1>
      </header>

      <div className="max-w-7xl mx-auto p-4 md:p-8">
        {/* Tabs */}
        <div className="flex gap-8 border-b mb-8">
          <button onClick={() => setActiveTab('services')} className={`pb-4 text-sm font-bold ${activeTab === 'services' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}>Tjenester</button>
          <button onClick={() => setActiveTab('assignments')} className={`pb-4 text-sm font-bold ${activeTab === 'assignments' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}>Tildelinger</button>
        </div>

        {activeTab === 'services' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {servicesToRender.map(service => (
              <div key={service.id} className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex gap-3">
                    <div className="p-3 bg-blue-50 text-blue-600 rounded-lg"><SafeIcon icon={getIconComponent(service.icon)} className="text-xl" /></div>
                    <div>
                      <h3 className="font-bold text-lg text-gray-900">{service.name}</h3>
                      <p className="text-blue-600 font-bold text-sm">{service.price},- <span className="text-gray-400 font-normal">/ {service.timeframe}</span></p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => { setSelectedService(service); setServiceFormData(service); setShowServiceModal(true); }} className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"><SafeIcon icon={FiEdit} /></button>
                    <button onClick={() => { setDeleteTarget(service); setShowDeleteModal(true); }} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"><SafeIcon icon={FiTrash2} /></button>
                  </div>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">{service.description}</p>
                <div className="mt-4 pt-4 border-t border-gray-50 flex justify-between items-center text-xs">
                    <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded font-mono uppercase tracking-wider">{service.category}</span>
                    <span className={`font-bold ${service.active ? 'text-green-600' : 'text-gray-400'}`}>{service.active ? 'Aktiv' : 'Inaktiv'}</span>
                </div>
              </div>
            ))}
            
            <button onClick={() => { setSelectedService(null); setServiceFormData({ name: '', type: 'subscription', description: '', price: '', category: 'external', features: [''], timeframe: 'mnd', icon: 'FiShield', active: true }); setShowServiceModal(true); }} className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-gray-400 hover:border-blue-500 hover:text-blue-500 hover:bg-blue-50 transition-all flex flex-col items-center justify-center gap-2 min-h-[200px]">
              <SafeIcon icon={FiPlus} className="text-3xl" />
              <span className="font-bold">Opprett ny tjeneste</span>
            </button>
          </div>
        )}

        {/* ... (Resten av koden for assignments tab er lik, men bruker servicesToRender) ... */}
        {activeTab === 'assignments' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="p-4 text-xs font-bold uppercase text-gray-500">Bedrift</th>
                  {servicesToRender.map(s => (
                    <th key={s.id} className="p-4 text-xs font-bold uppercase text-gray-500 text-center whitespace-nowrap">{s.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {companies.map(company => (
                  <tr key={company.id} className="hover:bg-gray-50">
                    <td className="p-4 font-medium text-gray-900">{company.name}</td>
                    {servicesToRender.map(service => {
                      const companyServices = Array.isArray(company.externalServices) ? company.externalServices : [];
                      const hasService = companyServices.includes(service.id);
                      return (
                        <td key={service.id} className="p-4 text-center">
                          <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase ${hasService ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
                            {hasService ? 'AKTIV' : '-'}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Service Modal */}
      <AnimatePresence>
        {showServiceModal && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
                <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white p-8 rounded-2xl max-w-md w-full shadow-2xl">
                    <h3 className="text-xl font-bold mb-6">{selectedService ? 'Rediger Tjeneste' : 'Ny Tjeneste'}</h3>
                    <form onSubmit={handleServiceSubmit} className="space-y-4">
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Navn</label>
                            <input type="text" required value={serviceFormData.name} onChange={e => setServiceFormData({...serviceFormData, name: e.target.value})} className="w-full p-3 border rounded-xl" />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Beskrivelse</label>
                            <textarea value={serviceFormData.description} onChange={e => setServiceFormData({...serviceFormData, description: e.target.value})} className="w-full p-3 border rounded-xl" rows="3" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Pris (kr)</label>
                                <input type="number" required value={serviceFormData.price} onChange={e => setServiceFormData({...serviceFormData, price: e.target.value})} className="w-full p-3 border rounded-xl" />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Periode</label>
                                <select value={serviceFormData.timeframe} onChange={e => setServiceFormData({...serviceFormData, timeframe: e.target.value})} className="w-full p-3 border rounded-xl bg-white">
                                    <option value="mnd">Per måned</option>
                                    <option value="år">Per år</option>
                                    <option value="sak">Per sak</option>
                                    <option value="gang">Engangs</option>
                                </select>
                            </div>
                        </div>
                        <div className="flex gap-3 pt-4">
                            <button type="button" onClick={() => setShowServiceModal(false)} className="flex-1 py-3 border border-gray-300 rounded-xl font-bold text-gray-600">Avbryt</button>
                            <button type="submit" className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-bold shadow-lg">Lagre</button>
                        </div>
                    </form>
                </motion.div>
            </div>
        )}
      </AnimatePresence>

      {/* Delete Modal */}
      <AnimatePresence>
        {showDeleteModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} className="bg-white p-8 rounded-2xl max-w-sm w-full shadow-2xl">
              <h3 className="text-xl font-bold mb-4 text-red-600">Slett tjeneste?</h3>
              <p className="text-gray-600 mb-6">Er du sikker på at du vil fjerne <b>{deleteTarget?.name}</b>? Dette kan ikke angres.</p>
              <div className="flex gap-4">
                <button onClick={() => setShowDeleteModal(false)} className="flex-1 py-3 bg-gray-100 rounded-xl font-bold text-gray-700">Avbryt</button>
                <button onClick={() => { deleteExternalService(deleteTarget.id); setShowDeleteModal(false); }} className="flex-1 py-3 bg-red-600 text-white rounded-xl font-bold">Slett</button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ExternalServicesManagement;